import type { FilterProps } from 'modules/core/components/Filters/types'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import type { Model } from 'modules/core/lib/mobx-rest'
import companies from 'modules/core/store/collections/companies'
import legalEntities from 'modules/core/store/collections/legalEntities'

import locations from 'modules/locations/store/collections/locations'
import teams from 'modules/teams/store/collections/teams'

import FILTER_OPTIONS from './filterOptions'

export const alphabeticallySort = (valueA: Model, valueB: Model) => {
  const aName = valueA.attributes.name
  const bName = valueB.attributes.name

  if (aName < bName) {
    return -1
  }

  if (aName > bName) {
    return 1
  }

  return 0
}

export interface FilterOption {
  label: string
  value: string
}

function employeeOptions({
  hideFirstEmployeeFilter,
  showFilterByDirectManagedEmployee,
  showFilterByManagedEmployee,
  showFilterByTimeoffSupervisor,
  showFilterByLocationIssues,
  showFilterByAway,
  showFilterByAwayThisMonth,
  showFilterByActive,
  showFilterByWelcomeSpaceAccess,
}: {
  hideFirstEmployeeFilter?: boolean
  showFilterByDirectManagedEmployee?: boolean
  showFilterByManagedEmployee?: boolean
  showFilterByTimeoffSupervisor?: boolean
  showFilterByLocationIssues?: boolean
  showFilterByAway?: boolean
  showFilterByAwayThisMonth?: boolean
  showFilterByActive?: boolean
  showFilterByWelcomeSpaceAccess?: boolean
}): FilterOption[] {
  const options: FilterOption[] = []

  if (!hideFirstEmployeeFilter) {
    options.push({
      label: i18n.t('employees.active'),
      value: FILTER_OPTIONS.activity.active,
    })
  }

  if (showFilterByDirectManagedEmployee) {
    options.push({
      label: i18n.t('employees.reporting_to_me_directly'),
      value: FILTER_OPTIONS.employees.directManaged,
    })
  }

  if (showFilterByManagedEmployee) {
    options.push({
      label: i18n.t('employees.reporting_to_me'),
      value: FILTER_OPTIONS.employees.managed,
    })
  }

  if (showFilterByTimeoffSupervisor) {
    options.push({
      label: i18n.t('employees.timeoff_managed_by_me'),
      value: FILTER_OPTIONS.employees.timeoffSupervisor,
    })
  }

  if (showFilterByLocationIssues) {
    options.push({
      label: i18n.t('employees.location_issues'),
      value: FILTER_OPTIONS.employees.locationIssues,
    })
  }

  if (showFilterByAway) {
    options.push({
      label: i18n.t('employees.away'),
      value: FILTER_OPTIONS.employees.away,
    })
  }

  if (showFilterByAwayThisMonth) {
    options.push({
      label: i18n.t('employees.away_this_month'),
      value: FILTER_OPTIONS.employees.awayThisMonth,
    })
  }

  if (showFilterByActive) {
    options.push(
      {
        label: i18n.t('employees.terminated'),
        value: FILTER_OPTIONS.activity.terminated,
      },
      {
        label: i18n.t('employees.all'),
        value: FILTER_OPTIONS.activity.all,
      }
    )
  }

  if (showFilterByWelcomeSpaceAccess && hasFeature('employee_onboarding')) {
    options.push({
      label: i18n.t('employees.with_welcome_space_access'),
      value: FILTER_OPTIONS.employees.welcomeSpaceAccess,
    })
  }

  return options
}

const computeOptions = async (props: FilterProps) => {
  const {
    hideFilterByStatus,
    showFilterByLegalEntity,
    showFilterByTeam,
    hideClockedInFilter,
    showShiftConflictsFilter,
    canSeeClockedIn,
  } = props

  const result: Record<string, FilterOption[]> = {}
  const employeeKey = i18n.t('filters.filter.employees')
  result[employeeKey] = employeeOptions(props)

  const locationKey = i18n.t('filters.filter.location')

  await Promise.all([
    locations.fetch({ withCachedFetchParams: true }),
    companies.fetch({ withCachedFetchParams: true }),
  ])

  result[locationKey] = locations.models
    .slice()
    .sort(alphabeticallySort)
    .map((location) => {
      return {
        label: location.attributes.name,
        value: FILTER_OPTIONS.location(location.attributes.id),
      }
    })

  if (showFilterByTeam) {
    await teams.fetch({ withCachedFetchParams: true })

    result[i18n.t('filters.filter.team')] = teams.models
      .slice()
      .sort(alphabeticallySort)
      .map((team) => {
        return {
          label: team.attributes.name,
          value: FILTER_OPTIONS.team(team.attributes.id),
        }
      })
  }

  if (showFilterByLegalEntity) {
    await legalEntities.fetch({ withCachedFetchParams: true })

    result[i18n.t('filters.filter.legal_entity')] = legalEntities.models.map((legalEntity) => {
      return {
        label: legalEntity.attributes.legal_name,
        value: FILTER_OPTIONS.legalEntity(legalEntity.attributes.id),
      }
    })
  }

  const statusKey = i18n.t('filters.filter.status')

  if (companies.selected && canSeeClockedIn && !hideFilterByStatus) {
    const statusOptions = [
      {
        label: i18n.t('filters.filter.clocked_in'),
        value: FILTER_OPTIONS.status.clockedIn,
      },
    ]

    if (companies.selected?.attributes.has_breaks_enabled) {
      statusOptions.push({
        label: i18n.t('filters.filter.on_break'),
        value: FILTER_OPTIONS.status.onBreak,
      })
    }

    result[statusKey] = statusOptions
  }

  if (showShiftConflictsFilter) {
    if (hideClockedInFilter) {
      result[statusKey] = [
        {
          label: i18n.t('filters.filter.shift_conflicts'),
          value: FILTER_OPTIONS.status.shiftConflicts,
        },
      ]
    } else {
      result[statusKey]?.push({
        label: i18n.t('filters.filter.shift_conflicts'),
        value: FILTER_OPTIONS.status.shiftConflicts,
      })
    }
  }

  return result
}

export default computeOptions
